import { ExperimentNames } from 'common/experiments/ExperimentDefinitions';
import { isDefined } from 'common/TypeUtilities';
import { isNil } from 'lodash';
import { FlexTreatment, Maybe } from 'src/gqlReactTypings.generated.d';
import { useMealPlanForUserStoreSelectors } from 'src/ztore/wss/meal-plan-for-user-store';
import { useExperiment } from './useExperiment';
import { useLogExposureOnce } from './useLogExposureOnce';

interface FlexServingsDecision {
  isFlexServings: boolean;
  flexTreatment: Maybe<FlexTreatment>;
  isFlexServingsResolved: boolean;
}

/**
 * Decides which flex experience, if any, this user gets.
 *
 * A user with a meal plan keeps the treatment the plan signed up into. A user without one is
 * bucketed by the V3 experiment while its flag is on, and by V2 otherwise, so V2 stops enrolling
 * new users the moment V3 launches.
 */
export const useFlexServingsDecision = (): FlexServingsDecision => {
  const v3 = useExperiment(ExperimentNames.WSS_FLEXIBLE_SERVINGS_V3);
  const v2 = useExperiment(ExperimentNames.WSS_FLEXIBLE_SERVINGS_V2);
  const mealPlan = useMealPlanForUserStoreSelectors.use.mealPlan?.();
  const mealPlanDataLoaded = useMealPlanForUserStoreSelectors.use.mealPlanDataLoaded?.();

  const flexServingsComesFromMealPlan = !!mealPlanDataLoaded && isDefined(mealPlan);
  const flexServingsComesFromExperiment = !!mealPlanDataLoaded && !isDefined(mealPlan);
  const isV3Enrolling = v3.isEligible;

  useLogExposureOnce(isV3Enrolling && flexServingsComesFromExperiment, v3.logExposure);
  useLogExposureOnce(!isV3Enrolling && v2.isEligible && flexServingsComesFromExperiment, v2.logExposure);

  const v3Treatment = v3.isTreatment ? FlexTreatment.FlexV3 : null;
  const v2Treatment = v2.isTreatment ? FlexTreatment.FlexV2 : null;
  const experimentTreatment = isV3Enrolling ? v3Treatment : v2Treatment;
  const flexTreatment = flexServingsComesFromMealPlan ? mealPlan?.flexTreatment ?? null : experimentTreatment;

  return {
    isFlexServings: !isNil(flexTreatment),
    flexTreatment,
    isFlexServingsResolved: !!mealPlanDataLoaded,
  };
};

export const useIsUserInFlexServings = (): boolean => useFlexServingsDecision().isFlexServings;
